import React from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { color } from '@/styles';
import { DEFECATION_ANALYTICS } from '@/constants/defecationAnalytics';
import { IGetDfreeBDeviceAnalyticsCareRecord } from '@/types/api/traversal/getDfreeBDeviceAnalytics';
import { formatLaxativeUnitJa } from '@/utils/dataLabelUtil';
import { IGetNotificationRes } from '@/types/api/notification/getNotification';
import { IGetResidentGasDetaSetsRes } from '@/types/api/bDevice/residentGasDataSets';
import { svgIcons } from '.';

dayjs.extend(isoWeek);

interface Props {
  startDate: string;
  endDate: string;
  careRecords?: IGetDfreeBDeviceAnalyticsCareRecord[];
  notifications?: IGetNotificationRes[];
  gasDataSets?: IGetResidentGasDetaSetsRes[];
}

interface IAggregatedRecord extends IGetDfreeBDeviceAnalyticsCareRecord {
  displayDefecationType: string;
  displayType: 'laxative' | 'defecation';
  uuid: string;
}

interface IAggregatedResult {
  [date: string]: IAggregatedRecord[];
}

interface ICalenderCircle {
  hour: number;
  statusColor: { fill: string; border: string };
}

const CalendarInfo = css`
  display: flex;
  justify-content: end;
  gap: 0 14px;
  margin-right: 4px;
`;

const CalendarHeaderContainerStyle = css`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const HeaderStyle = css`
  background-color: transparent;
  text-align: center;
  padding: 8px 4px;
  font-size: 10px;
  color: ${color.gray};
  background-color: ${color.white};
`;

const CalendarCellContainerStyle = css`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  background: ${color.thin_gray};
  gap: 1px;
  border: 1px solid ${color.thin_gray};
  overflow: hidden;
`;

const DateCellStyle = css`
  display: flex;
  flex-direction: column;
  background: ${color.white};
  text-align: center;
  min-height: 120px;
  font-size: 14px;
`;

const DateCellExceptTimeline = css`
  padding: 4px 4px 8px 4px;
  flex: 1;
`;

const OutRangeCellStyle = css`
  background: ${color.bg_light_gray};
`;

const DefecationCellStyle = css`
  background: #fbf4e1;
`;

const LaxativeCardContainer = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.4em;
  margin-top: 16px;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 9px;
  background: ${color.table_green};
`;

const LaxativeCardTitle = css`
  text-align: left;
`;

const LaxativeCardText = css`
  display: flex;
  white-space: nowrap;
  font-weight: bold;
  color: ${color.dark_gray};
  gap: 2px;
`;

const EllipsisText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DefecationIconContainer = css`
  display: flex;
  justify-content: center;
  position: relative;
  gap: 4px;
  margin-top: 12px;
`;

const ResizeIcon = css`
  max-width: 24px;
`;

const DefecationIconStyle = css`
  position: relative;
  width: 24px;
  height: 24px;
`;

const LeakOutIconStyle = css`
  position: absolute;
  top: -5px;
  right: -5px;
`;

const DefecationCount = css`
  position: absolute;
  font-size: 7px;
  bottom: -6px;
  right: 14px;
`;

const DateTextStyle = css`
  font-size: 10px;
  color: ${color.gray};
`;

const TooltipText = css`
  display: flex;
  flex-direction: row;
  gap: 0 6px;
`;

const TooltipColorText = css`
  color: #eac159;
`;

const TimelineContainer = (isMedical: boolean) => css`
  position: relative;
  height: 10px;
  display: flex;
  background-color: ${isMedical ? 'transparent' : color.bg_gray_dark};
`;

const TimeBlock = (hour: number, isPoppedNotification: boolean, isMedical: boolean) => css`
  position: relative;
  flex: 1;
  background-color: ${isPoppedNotification ? color.notification_orange : 'transparent'};

  &::after {
    content: '';
    position: absolute;
    top: ${isMedical ? '50%' : '0'};
    right: 0;
    width: 1px;
    height: ${isMedical ? '50%' : '100%'};
    background-color: ${hour !== 23 && hour % 6 === 5 ? color.thin_gray : 'transparent'};
  }
`;

const RecordsCircle = (colorCode: ICalenderCircle['statusColor'], isMedical: boolean) => css`
  position: absolute;
  bottom: ${isMedical ? 'calc(1px + 50%)' : 'calc(1px + 100%)'};
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% + 1px); // 親より少し小さくなるので+1px
  max-width: 5px;
  height: auto;
  aspect-ratio: 1/1;
  background-color: ${colorCode.fill};
  border: 1px solid ${colorCode.border};
  border-radius: 50vh;
`;

const ConnectedTimeBg = (start: number, duration: number) => css`
  position: absolute;
  left: ${`${(start / 1440) * 100}%`};
  max-width: 100%;
  width: ${`${(duration / 1440) * 100}%`};
  height: 100%;

  // バー
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 100%;
    background-color: ${color.light_orange};
  }
`;

const LegendContainer = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const legendBlock = (colorCode: ICalenderCircle['statusColor']) => css`
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: ${colorCode.fill};
  border: 1px solid ${colorCode.border};
  border-radius: 50vh;
`;

const legendLine = (colorCode: ICalenderCircle['statusColor']) => css`
  display: inline-block;
  width: 4px;
  height: 12px;
  background-color: ${colorCode.fill};
  margin-right: 4px;
`;

export const DefecationAnalyticsCalendar = ({ startDate, endDate, careRecords, notifications, gasDataSets }: Props) => {
  const { t, i18n } = useTranslation();
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const dayOfWeek = ['月', '火', '水', '木', '金', '土', '日'];
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const calendarStart = start.startOf('isoWeek');
  const days = Array.from({ length: 42 }, (_, i) => calendarStart.add(i, 'day').format('YYYY-MM-DD'));
  const statusColors = {
    defecation: { fill: '#E0BC00', border: '#C0A829' },
    laxative: { fill: '#B5B5B5', border: '#A2A2A2' },
    benActive: {fill: '#FFC27A', border: ''},
    gasPooped: {fill: '#FF8A00', border: ''},
  };

  const DateDefecation = React.useCallback(
    ({ records }: { records: IAggregatedRecord[] }) => {
      const defecationDescRecords = records.filter((record) => record.displayType === 'defecation').reverse();
      return (
        <div css={DefecationIconContainer}>
          {defecationDescRecords.slice(0, 3).map((record) => (
            <div key={record.uuid} css={DefecationIconStyle}>
              <img css={ResizeIcon} src={svgIcons[record.displayDefecationType]} alt={record.displayDefecationType} />
              {record.isLeakOutFeces && <img css={LeakOutIconStyle} src={svgIcons['leakOut']} alt={'leakOut'} />}
            </div>
          ))}
          {defecationDescRecords.length > 3 && <div css={DefecationCount}>+{defecationDescRecords.length - 3}</div>}
        </div>
      );
    },
    [careRecords]
  );

  const DateLaxative = React.useCallback(
    ({ records }: { records: IAggregatedRecord[] }) => {
      const laxativeRecords = records.filter((record) => record.displayType === 'laxative');
      const latest = laxativeRecords.length ? laxativeRecords[laxativeRecords.length - 1] : null;

      return (
        latest && (
          <div css={LaxativeCardContainer}>
            <span css={LaxativeCardTitle}>{dayjs(latest.timestamp).format('HH:mm')}</span>
            <div css={LaxativeCardText}>
              <span css={EllipsisText}>{latest.laxativeName}</span>
              <span>:</span>
              <span>
                {latest.laxativeAmount ?? ''}
                {formatLaxativeUnitJa(latest.laxativeUnit) ?? ''}
              </span>
            </div>
          </div>
        )
      );
    },
    [careRecords]
  );

  const DateTimeline = React.useCallback(
    ({
      gasPoppedTimeByHour,
      defecationLaxativeRecordsByHour,
      gasDataSets,
    }: {
      gasPoppedTimeByHour: number[];
      defecationLaxativeRecordsByHour: ICalenderCircle[];
      gasDataSets: IGetResidentGasDetaSetsRes[];
    }) => {
      return (
        <div css={TimelineContainer(showMedicalContent)}>
          {gasDataSets?.map((v, idx) => {
            const startOfDay = dayjs(v.startDateTime).startOf('day');
            const startMinutes = dayjs(v.startDateTime).diff(startOfDay, 'minute');
            const duration = dayjs(v.endDateTime).diff(v.startDateTime, 'minute');
            return <div key={idx} css={ConnectedTimeBg(startMinutes, duration)}></div>;
          })}
          {[...Array(24)].map((_, i) => (
            <div key={i} css={TimeBlock(i, gasPoppedTimeByHour.includes(i), showMedicalContent)}>
              {defecationLaxativeRecordsByHour.some((v) => v.hour === i) && (
                <span
                  css={RecordsCircle(
                    defecationLaxativeRecordsByHour.find((v) => v.hour === i)
                      ?.statusColor as ICalenderCircle['statusColor'],
                    showMedicalContent,
                  )}
                ></span>
              )}
            </div>
          ))}
        </div>
      );
    },
    [showMedicalContent],
  );

  const RenderDateCell = React.memo(
    ({
      date,
      records,
      gasPoppedTimeByHour,
      defecationLaxativeRecordsByHour,
      gasDataSets,
    }: {
      date: string;
      records: IAggregatedRecord[];
      gasPoppedTimeByHour: number[];
      defecationLaxativeRecordsByHour: ICalenderCircle[];
      gasDataSets: IGetResidentGasDetaSetsRes[];
    }) => {
      const currentDate = dayjs(date);
      const isOutRange = !(currentDate.isAfter(start.subtract(1, 'day')) && currentDate.isBefore(end.add(1, 'day')));
      const hasDefecationRecord = records.some((v) => v.displayType === 'defecation');
      const isFirstDay = currentDate.date() === 1;

      return (
        <Tooltip
          title={tooltipItem(records)}
          key={currentDate.format('YYYYMMDD')}
          arrow
          placement='top'
          componentsProps={{
            tooltip: { sx: { background: color.dark_gray, maxWidth: '600px' } },
            arrow: { sx: { color: color.dark_gray } },
          }}
        >
          <div
            key={date}
            css={[DateCellStyle, isOutRange && OutRangeCellStyle, hasDefecationRecord && DefecationCellStyle]}
          >
            <div css={DateCellExceptTimeline}>
              <span css={DateTextStyle}>
                {isFirstDay ? dayjs(date).format('M/') + dayjs(date).date() : dayjs(date).date()}
              </span>
              <DateDefecation records={records} />
              <DateLaxative records={records} />
            </div>
            <DateTimeline
              gasPoppedTimeByHour={gasPoppedTimeByHour}
              defecationLaxativeRecordsByHour={defecationLaxativeRecordsByHour}
              gasDataSets={gasDataSets}
            />
          </div>
        </Tooltip>
      );
    }
  );

  const tooltipItem = React.useCallback(
    (records: IAggregatedRecord[]) => {
      const hardnessTypeLabels = i18n.language.startsWith('ja')
        ? DEFECATION_ANALYTICS.HARDNESS_TYPES_JA
        : DEFECATION_ANALYTICS.HARDNESS_TYPES_EN;
      const formatHardnessType = (type: string) => hardnessTypeLabels[type as keyof typeof hardnessTypeLabels] ?? type;
      const volumeTypeLabels = i18n.language.startsWith('ja')
        ? DEFECATION_ANALYTICS.VOLUME_TYPES_JA
        : DEFECATION_ANALYTICS.VOLUME_TYPES_EN;
      const formatVolumeType = (type: string) => volumeTypeLabels[type as keyof typeof volumeTypeLabels] ?? type;
      return records.length > 0
        ? records.map((record) => (
            <div key={record.id + record.displayType} css={TooltipText}>
              {record.displayType === 'defecation' && (
                <>
                  <span css={TooltipColorText}>{dayjs(record.timestamp).format('HH:mm')}</span>
                  {record.hardnessType && <span css={TooltipColorText}>{formatHardnessType(record.hardnessType)}</span>}
                  {record.defecationVolumeType && (
                    <span css={TooltipColorText}>{formatVolumeType(record.defecationVolumeType)}</span>
                  )}
                  {record.isLeakOutFeces && <span css={TooltipColorText}>{t('fecalContamination', '便汚染')}</span>}
                </>
              )}
              {record.displayType === 'laxative' && (
                <>
                  <span>{dayjs(record.timestamp).format('HH:mm')}</span>
                  <span>{record.laxativeName ?? ''}</span>
                  <span>
                    {record.laxativeAmount ?? ''}
                    {formatLaxativeUnitJa(record.laxativeUnit) ?? ''}
                  </span>
                </>
              )}
            </div>
          ))
        : '';
    },
    [careRecords]
  );

  const aggregateCareRecords = React.useMemo((): IAggregatedResult => {
    const result: IAggregatedResult = {};

    let currentDate = start.clone();
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      result[currentDate.format('YYYY-MM-DD')] = [];
      currentDate = currentDate.add(1, 'day');
    }

    careRecords?.forEach((r) => {
      const dateKey = dayjs(r.timestamp).format('YYYY-MM-DD');

      const shouldIncludeDefecation =
        r.defecationVolumeType !== 'none' &&
        r.defecationVolumeType !== null &&
        (r.defecationVolumeType === 'bit' || r.hardnessType !== null);

      const shouldIncludeLaxative = r.laxativeId !== null && r.laxativeType === 'stimulant';

      if (shouldIncludeDefecation && result[dateKey]) {
        result[dateKey].push({
          ...r,
          uuid: uuid(),
          displayType: 'defecation',
          displayDefecationType: r.defecationVolumeType === 'bit' ? 'bit' : (r.hardnessType as string),
        });
      }

      if (shouldIncludeLaxative && result[dateKey]) {
        result[dateKey].push({
          ...r,
          uuid: uuid(),
          displayType: 'laxative',
          displayDefecationType: '',
        });
      }
    });

    Object.keys(result).forEach((date) => {
      result[date].sort((a, b) => dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix());
    });

    return result;
  }, [careRecords]);

  // 各日のでたかも通知の時間を0:00からの時間カウントにしてまとめる
  const gasPoppedTimeByHourEachDay = React.useMemo(() => {
    const result: { [date: string]: number[] } = {};
    let currentDate = start.clone();
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      result[currentDate.format('YYYY-MM-DD')] = [];
      currentDate = currentDate.add(1, 'day');
    }

    notifications?.forEach((r) => {
      const dateKey = dayjs(r.timestamp).format('YYYY-MM-DD');
      const startOfDay = dayjs(r.timestamp).startOf('day');
      const diffByHour = dayjs(r.timestamp).diff(startOfDay, 'hour');

      if (result[dateKey] && !result[dateKey].includes(diffByHour) && r.notificationType === 'gasPooped') {
        result[dateKey].push(diffByHour);
      }
    });
    return result;
  }, [notifications]);

  // 通知の成功有無を0:00からの時間カウントにしてまとめる（排便記録/下剤記録）
  const defecationLaxativeRecordsByHourEachDay = React.useMemo(() => {
    const result: { [date: string]: ICalenderCircle[] } = {};

    let currentDate = start.clone();
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      result[currentDate.format('YYYY-MM-DD')] = [];
      currentDate = currentDate.add(1, 'day');
    }

    careRecords?.forEach((c) => {
      const dateKey = dayjs(c.timestamp).format('YYYY-MM-DD');
      const startOfDay = dayjs(c.timestamp).startOf('day');
      const diffByHour = dayjs(c.timestamp).diff(startOfDay, 'hour');

      if (result[dateKey]) {
        const data = result[dateKey].find((v) => v.hour === diffByHour);
        if (!!c.defecationVolumeType && c.defecationVolumeType !== 'none') {
          if (!data) {
            result[dateKey].push({ hour: diffByHour, statusColor: statusColors.defecation });
          } else {
            const idx = result[dateKey].findIndex((v) => v.hour === diffByHour);
            result[dateKey][idx] = { hour: diffByHour, statusColor: statusColors.defecation };
          }
        } else if (c.laxativeId !== null && c.laxativeType === 'stimulant') {
          // 同じ時間帯に便の記録があったら、便を優先（更新しない）
          if (!data || data.statusColor !== statusColors.defecation) {
            result[dateKey].push({ hour: diffByHour, statusColor: statusColors.laxative });
          }
        }
      }
    });
    return result;
  }, [careRecords]);

  // 各日の便デバイスのデータセットをまとめる
  const gasDataSetsEachDay = React.useMemo(() => {
    const result: { [date: string]: IGetResidentGasDetaSetsRes[] } = {};
    let currentDate = start.clone();
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      result[currentDate.format('YYYY-MM-DD')] = [];
      currentDate = currentDate.add(1, 'day');
    }
    gasDataSets?.forEach((r) => {
      const startDateKey = dayjs(r.startDateTime).format('YYYY-MM-DD');
      const endDateKey = dayjs(r.endDateTime).format('YYYY-MM-DD');

      const diffDate = dayjs(endDateKey).diff(startDateKey, 'day');
      for (let i = 0; i <= diffDate; i++) {
        const key = dayjs(r.startDateTime).add(i, 'day').format('YYYY-MM-DD');

        // 開始と終了の日付が異なる場合、時間を調整（当日中の開始・終了になるようにする）
        const start = dayjs(key).isSame(r.startDateTime, 'day')
          ? r.startDateTime
          : dayjs(key).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
        const end = dayjs(key).isSame(r.endDateTime, 'day')
          ? r.endDateTime
          : dayjs(key).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');

        if (result[key]) {
          result[key].push({ ...r, startDateTime: start, endDateTime: end });
        }
      }
    });
    return result;
  }, [gasDataSets]);

  const calendarCellData = React.useMemo(
    () =>
      days.map((day) => ({
        day,
        records: aggregateCareRecords[day] || [],
        gasPoppedTimeByHour: showMedicalContent ? [] : gasPoppedTimeByHourEachDay[day] || [],
        defecationLaxativeRecordsByHour: defecationLaxativeRecordsByHourEachDay[day] || [],
        gasDataSets: showMedicalContent ? [] : gasDataSetsEachDay[day] || [],
      })),
    [
      days,
      aggregateCareRecords,
      showMedicalContent,
      gasPoppedTimeByHourEachDay,
      defecationLaxativeRecordsByHourEachDay,
      gasDataSetsEachDay,
    ],
  );

  return (
    <>
      <div css={CalendarInfo}>
        <div css={LegendContainer}>
          <img src={svgIcons['leakOut']} alt={'leakOut'} />
          <span>{t('fecalContamination', '便汚染')}</span>
        </div>
        <div css={LegendContainer}>
          <img src={svgIcons['bit']} alt={'bit'} />
          <span>{t('soiled', '付着')}</span>
        </div>
        <div css={LegendContainer}>
          <span css={legendBlock(statusColors.defecation)}></span>
          <span>{t('defecationRecord', '排便記録')}</span>
        </div>
        <div css={LegendContainer}>
          <span css={legendBlock(statusColors.laxative)}></span>
          <span>{t('laxativeRecord', '下剤記録')}</span>
        </div>
        {!showMedicalContent && (
          <div css={LegendContainer}>
            <span css={legendLine(statusColors.benActive)} />
            <span>{t('benRunningTime', 'Ben駆動時間')}</span>
          </div>
        )}
        {!showMedicalContent && (
          <div css={LegendContainer}>
            <span css={legendLine(statusColors.gasPooped)} />
            <span>{t('odorNotification', '臭い検知通知')}</span>
          </div>
        )}
      </div>
      <div css={CalendarHeaderContainerStyle}>
        {dayOfWeek.map((day) => (
          <div key={day} css={HeaderStyle}>
            {day}
          </div>
        ))}
      </div>
      <div css={CalendarCellContainerStyle}>
        {calendarCellData.map((cell) => (
          <RenderDateCell
            key={cell.day}
            date={cell.day}
            records={cell.records}
            gasPoppedTimeByHour={cell.gasPoppedTimeByHour}
            defecationLaxativeRecordsByHour={cell.defecationLaxativeRecordsByHour}
            gasDataSets={cell.gasDataSets}
          />
        ))}
      </div>
    </>
  );
};
